import { useState } from 'react';
import { Inbox } from 'lucide-react';
import { FinalidadeEstado, type EstadoDto, type ProcessoCartaoDto, type QuadroDto } from '@nexital/shared';
import { useQuadro } from '../../application/dependencias';
import { ARRASTE_SEM_ESTADO, useSoltura } from './use-soltura';

const nada = () => {};

/** Cartões sem estado e os estados que têm grupo no quadro (destinos possíveis). */
function bandeja(dados: QuadroDto | null) {
  if (!dados) return { nome: 'Aguardando análise', cartoes: [], destinos: [] };
  const semEstado = dados.estados.find((e) => e.finalidade === FinalidadeEstado.SemEstado);
  const comGrupo = new Set(dados.grupos.map((g) => g.estadoUuid));
  return {
    nome: semEstado?.nome ?? 'Aguardando análise',
    cartoes: dados.processos.filter((p) => p.estadoUuid === semEstado?.uuid),
    destinos: dados.estados.filter((e) => comGrupo.has(e.uuid)),
  };
}

type PropsCartao = { processo: ProcessoCartaoDto; destinos: EstadoDto[]; enviar: (processoUuid: string, estadoUuid: string) => void };

function CartaoSemEstado({ processo, destinos, enviar }: PropsCartao) {
  return (
    <li
      className="sem-estado__cartao"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(ARRASTE_SEM_ESTADO, processo.uuid);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <strong>{processo.numero}</strong>
      <span className="sem-estado__objeto">{processo.objeto}</span>
      <select aria-label={`Mover ${processo.numero} para um grupo`} value="" onChange={(e) => e.target.value && enviar(processo.uuid, e.target.value)}>
        <option value="">Mover para…</option>
        {destinos.map((estado) => (
          <option key={estado.uuid} value={estado.uuid}>
            {estado.nome}
          </option>
        ))}
      </select>
    </li>
  );
}

/**
 * Bandeja translúcida, acima da Lixeira, com os cartões sem estado (D3, D9, RQ-EST-009). Cada cartão pode ser
 * arrastado para um grupo do quadro ou, pelo teclado, enviado a um grupo pela lista "Mover para…".
 */
export function SemEstado() {
  const dados = useQuadro((s) => s.dados);
  const { enviarParaGrupo } = useSoltura(nada);
  const [aberta, definirAberta] = useState(false);
  const { nome, cartoes, destinos } = bandeja(dados);

  return (
    <section className={`sem-estado${aberta ? ' sem-estado--aberta' : ''}`} aria-label="Cartões sem estado">
      <button type="button" className="sem-estado__botao" onClick={() => definirAberta(!aberta)} aria-expanded={aberta}>
        <Inbox size={18} aria-hidden />
        <span>{nome}</span>
        {cartoes.length > 0 && <span className="bandeja__total" aria-label={`${cartoes.length} processo(s)`}>{cartoes.length}</span>}
      </button>
      {aberta && cartoes.length > 0 && (
        <ul className="sem-estado__lista">
          {cartoes.map((p) => (
            <CartaoSemEstado key={p.uuid} processo={p} destinos={destinos} enviar={enviarParaGrupo} />
          ))}
        </ul>
      )}
      {aberta && (
        <p className="sem-estado__vazio">{cartoes.length ? 'Arraste um cartão daqui para um grupo.' : 'Nenhum cartão sem estado.'}</p>
      )}
    </section>
  );
}
